import { laptopMediumMax } from '@propertypal/shared/src/constants/mediaQueries';
import useInView from '@propertypal/shared/src/hooks/useInView';
import useWindowSize from '@propertypal/shared/src/hooks/useWindowSize';
import Apartments from '@propertypal/shared/src/resources/icons/filters/apartments-icon.svg';
import Detached from '@propertypal/shared/src/resources/icons/filters/detached-icon.svg';
import SemiDetached from '@propertypal/shared/src/resources/icons/filters/semi-detached-icon.svg';
import Terraced from '@propertypal/shared/src/resources/icons/filters/terraced-icon.svg';
import { SoldPriceData } from '@propertypal/shared/src/types/soldPriceData';
import {
  ELECTORAL_AREA_LINK,
  formatSoldPrices,
  formatSoldVolume,
  moneyFormatter,
  NI_HOUSE_PRICE_LINK,
} from '@propertypal/shared/src/utils/property/chartUtils';
import getFormattedPrice from '@propertypal/shared/src/utils/property/getFormattedPrice';
import React, { FunctionComponent, useRef } from 'react';
import Loader from 'react-spinners/PulseLoader';
import { useTheme } from 'styled-components';
import LineChart from '../charts/LineChart';
import useMediaQuery from '../media-query/useMediaQuery';
import { SmallHeading, SubHeading, Text, LinkText } from '../typography';
import {
  ChartContainer,
  Container,
  ExtraContainer,
  LoadingBox,
  StyleStats,
  StyleStatsWrapper,
  Wrapper,
} from './AreaPriceTracker.style';

interface Props {
  soldPriceData?: SoldPriceData;
  loading?: boolean;
  onInView?: () => void;
  error?: any;
}

const AreaPriceTracker: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const container = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery({ query: `(${laptopMediumMax})` });
  const windowSize = useWindowSize();
  useInView(props.onInView, container, props.soldPriceData);

  if (windowSize.width === undefined || props.loading)
    return (
      <LoadingBox ref={container} data-testid="areaPriceTrackerLoading">
        <Loader color={theme.backgroundLight} />
        <Text mt={15}>Loading Area Price Data</Text>
      </LoadingBox>
    );

  if (props.error)
    return (
      <LoadingBox data-testid="area-price-error" bg={theme.backgroundLighter}>
        <Text mt={15}>Area Price Data Unavailable</Text>
      </LoadingBox>
    );

  if (!props.soldPriceData) return <div ref={container} />;

  const { soldPriceData } = props;

  const chartWidth = isMobile ? windowSize.width - 30 : Math.min(windowSize.width * 0.6, 780);
  const chartHeight = isMobile ? 350 : 450;
  const soldVolumeData = formatSoldVolume(soldPriceData);
  const firstYear = soldVolumeData[0].data[0].key;
  const lastYear = soldVolumeData[0].data[soldVolumeData[0].data.length - 1].key;
  const formattedSoldPrices = formatSoldPrices(soldPriceData);

  return (
    <Wrapper data-testid="area-price-tracker">
      {!isMobile && (
        <SubHeading mt={60} mb={30}>
          Area Price Tracker
        </SubHeading>
      )}

      <Container data-testid="areaPriceTracker">
        <ChartContainer>
          {soldPriceData?.areaForecast && soldPriceData.areaStatistics && (
            <SmallHeading fontSize={20} fontWeight="normal">
              Typical Property Prices in {soldPriceData.areaForecast.maximumYearForeCastSoldPrice.area.name},{' '}
              {firstYear}-{lastYear}
            </SmallHeading>
          )}

          <LineChart
            width={chartWidth}
            height={chartHeight}
            lines={formattedSoldPrices}
            labelFormatter={moneyFormatter}
            bubbleTextFormatter={(value) => getFormattedPrice(value, '£')}
            showLabels
          />
        </ChartContainer>

        <ExtraContainer>
          <SmallHeading fontSize={20} fontWeight="normal" mb={30}>
            Typical Property Prices {lastYear}
          </SmallHeading>

          <StyleStatsWrapper>
            {formattedSoldPrices.map((soldPrices) => {
              const forecast = soldPrices.data[soldPrices.data.length - 1].value;

              return (
                <StyleStats key={soldPrices.key}>
                  {soldPrices.key === 'Detached' && <Detached viewBox="0 0 116 86" />}
                  {soldPrices.key === 'Apartments' && <Apartments viewBox="0 0 91 93" />}
                  {soldPrices.key === 'Semi Detached' && <SemiDetached viewBox="0 0 124 86" />}
                  {soldPrices.key === 'Terraced' && <Terraced viewBox="0 0 118 87" />}

                  <div>
                    <Text>{soldPrices.key}</Text>
                    <Text fontSize={36} color={soldPrices.color}>
                      {getFormattedPrice(forecast, '£')}
                    </Text>
                  </div>
                </StyleStats>
              );
            })}
          </StyleStatsWrapper>
        </ExtraContainer>
      </Container>

      <Container>
        <ChartContainer>
          {soldPriceData?.areaForecast && (
            <SmallHeading fontSize={20} fontWeight="normal">
              Number of Properties Sold in {soldPriceData.areaForecast.maximumYearForeCastSoldPrice.area.name},{' '}
              {firstYear}-{lastYear}
            </SmallHeading>
          )}

          <LineChart
            width={chartWidth}
            height={chartHeight}
            lines={formatSoldVolume(soldPriceData)}
            bubbleTextFormatter={(value) => `Sales: ${value}`}
          />
        </ChartContainer>

        <ExtraContainer>
          <Text fontWeight="bold" fontSize={14}>
            DISCLAIMER
          </Text>

          <Text fontSize={12} mt={10}>
            Unless otherwise stated, all content in this section is copyright of PropertyPal 2019, and all rights are
            reserved. The contents of this section, either in whole or in part, may not be reproduced, stored or
            transmitted without prior written permission from PropertyPal.
          </Text>

          <Text fontSize={12} mt={10}>
            This section has been prepared by PropertyPal for general guidance on matters of interest only, and does not
            constitute professional advice. You should not act upon the information contained in this section without
            obtaining specific professional advice. No representation or warranty (express or implied) is given as to
            the accuracy or completeness of the information contained in this section.
          </Text>

          <Text fontSize={12} mt={10}>
            It is a condition of PropertyPal allowing you free access to this section that you accept that PropertyPal,
            its clients, employees and agents do not accept or assume any liability, responsibility or duty of care for
            any consequences of you or anyone else acting, or refraining to act, in reliance on the information
            contained in this section or for any decision based on it.
          </Text>

          <Text fontSize={12} mt={10}>
            *Note: Dataset is compiled by PropertyPal using a combination of public data from the official NI House
            Price Index publication, released by Land & Property Services in conjunction with the Northern Ireland
            Statistics and Research Agency, and supplemented with data collected by PropertyPal. The NI House Price
            Index can be accessed <LinkText href={NI_HOUSE_PRICE_LINK}>here</LinkText>. The data has been cleansed to
            remove anomalies and statistical analysis undertaken by PropertyPal to comprise a timely and backdated
            consistent series to 2005. Locations provided are based on the latest electoral area boundaries provided by
            the District Electoral Areas Commissioner and can be accessed{' '}
            <LinkText href={ELECTORAL_AREA_LINK}>here</LinkText>.
          </Text>
        </ExtraContainer>
      </Container>
    </Wrapper>
  );
};

export default AreaPriceTracker;
